// Authentication helpers
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

export function initAuth() {
  const token = localStorage.getItem('token') || localStorage.getItem('authToken');
  const userEmail = localStorage.getItem('userEmail');
  
  const loginBtn = document.getElementById('loginBtn');
  const registerBtn = document.getElementById('registerBtn');
  const userMenu = document.getElementById('userMenu');
  const userEmailEl = document.getElementById('userEmail');
  
  // Update third nav link on homepage
  const thirdNavLink = document.getElementById('thirdNavLink');
  const thirdDot = document.getElementById('thirdDot');
  const thirdDotLabel = document.getElementById('thirdDotLabel');
  
  // Get mobile dot navigation container
  const mobileDotNav = document.querySelector('.mobile-dot-nav');
  
  if (token && userEmail) {
    // User is logged in
    if (loginBtn) loginBtn.classList.add('hidden');
    if (registerBtn) registerBtn.classList.add('hidden');
    if (userMenu) userMenu.classList.remove('hidden');
    if (userEmailEl) userEmailEl.textContent = userEmail;
    
    // Show mobile dot navigation when logged in
    if (mobileDotNav) mobileDotNav.classList.remove('hidden');
    
    // Change Earn to Profile on homepage when logged in
    if (thirdNavLink) {
      thirdNavLink.textContent = 'Profile';
      thirdNavLink.setAttribute('title', 'Profile');
    }
    if (thirdDot) {
      thirdDot.setAttribute('title', 'Profile');
    }
    if (thirdDotLabel) {
      thirdDotLabel.textContent = 'Profile';
    }
  } else {
    // User is not logged in
    if (loginBtn) loginBtn.classList.remove('hidden');
    if (registerBtn) registerBtn.classList.remove('hidden');
    if (userMenu) userMenu.classList.add('hidden');
    
    // Hide mobile dot navigation when not logged in
    if (mobileDotNav) mobileDotNav.classList.add('hidden');
    
    // Keep Earn on homepage when not logged in
    if (thirdNavLink) {
      thirdNavLink.textContent = 'Earn';
      thirdNavLink.setAttribute('title', 'Earn');
    }
    if (thirdDot) {
      thirdDot.setAttribute('title', 'Earn');
    }
    if (thirdDotLabel) {
      thirdDotLabel.textContent = 'Earn';
    }
  }
}

export function getAuthToken() {
  return localStorage.getItem('token') || localStorage.getItem('authToken');
}

export function setAuthToken(token, email) {
  localStorage.setItem('authToken', token);
  localStorage.setItem('userEmail', email);
}

export function clearAuthToken() {
  localStorage.removeItem('authToken');
  localStorage.removeItem('token');
  localStorage.removeItem('userEmail');
  localStorage.removeItem('user');
  console.log('[clearAuthToken] Cleared all auth data from localStorage');
}

// Login function
export async function handleLogin(email, password) {
  try {
    const response = await fetch(`${API_URL}/api/v1/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ email, password })
    });

    const data = await response.json();

    if (response.ok && data.token) {
      setAuthToken(data.token, email);
      return { success: true, data };
    } else {
      return { success: false, message: data.message || 'Login failed' };
    }
  } catch (error) {
    console.error('Login error:', error);
    return { success: false, message: 'Network error. Please try again.' };
  }
}

// Signup function
export async function handleSignup(formData) {
  try {
    const response = await fetch(`${API_URL}/api/v1/auth/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(formData)
    });

    const data = await response.json();

    if (response.ok) {
      return { success: true, data };
    } else {
      return { success: false, message: data.message || 'Registration failed' };
    }
  } catch (error) {
    console.error('Signup error:', error);
    return { success: false, message: 'Network error. Please try again.' };
  }
}

// Get user profile
export async function getUserProfile() {
  const token = getAuthToken();
  if (!token) {
    console.error('[getUserProfile] No token found');
    return null;
  }

  console.log('[getUserProfile] Token found:', token.substring(0, 20) + '...');

  try {
    const url = `${API_URL}/api/v1/users/profile`;
    console.log('[getUserProfile] Fetching from:', url);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    console.log('[getUserProfile] Response status:', response.status);

    if (response.ok) {
      const data = await response.json();
      console.log('[getUserProfile] Profile loaded successfully');
      return data.user || data;
    } else if (response.status === 401 || response.status === 404) {
      // Token invalid or user not found - clear and redirect
      let errorData;
      try {
        errorData = await response.json();
      } catch (e) {
        const text = await response.text();
        console.error('[getUserProfile] Response text:', text);
        errorData = { error: text };
      }
      console.error('[getUserProfile] Auth failed:', response.status, errorData);
      
      // Add a small delay before redirecting so we can see the logs
      setTimeout(() => {
        clearAuthToken();
        window.location.href = '/login.html';
      }, 1000);
      return null;
    }
    const errorData = await response.json().catch(() => ({}));
    console.error('[getUserProfile] Unexpected response:', response.status, errorData);
    return null;
  } catch (error) {
    console.error('[getUserProfile] Network/fetch error:', error);
    return null;
  }
}

// Update user profile
export async function updateUserProfile(updateData) {
  const token = getAuthToken();
  if (!token) return { success: false, message: 'Not authenticated' };

  try {
    const response = await fetch(`${API_URL}/api/v1/users/profile`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(updateData)
    });

    const data = await response.json();

    if (response.ok) {
      return { success: true, data };
    } else {
      return { success: false, message: data.message || 'Update failed' };
    }
  } catch (error) {
    console.error('Profile update error:', error);
    return { success: false, message: 'Network error. Please try again.' };
  }
}

// Update password
export async function updatePassword(currentPassword, newPassword) {
  const token = getAuthToken();
  if (!token) return { success: false, message: 'Not authenticated' };

  try {
    const response = await fetch(`${API_URL}/api/v1/users/password`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ currentPassword, newPassword })
    });

    const data = await response.json();

    if (response.ok) {
      return { success: true, data };
    } else {
      return { success: false, message: data.message || 'Password update failed' };
    }
  } catch (error) {
    console.error('Password update error:', error);
    return { success: false, message: 'Network error. Please try again.' };
  }
}
